import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Users, Clock, AlarmClock, CheckCircle2, Sunrise, Plane,
  ChevronUp, ChevronDown, Download,
} from "lucide-react";
import { API_URL } from "../../api/api";
import { T, Avatar, SearchInput, Select } from "../../components/em/ui";

const SECTIONS = [
  { key: "notMarked", label: "Not Yet Marked", short: "Not Marked", color: T.fg5, bg: T.bgMuted, Icon: Clock },
  { key: "late", label: "Late Arrival", short: "Late", color: "#b45309", bg: "rgba(251,191,36,0.12)", Icon: AlarmClock },
  { key: "onTime", label: "On Time", short: "On Time", color: T.success600, bg: T.success50, Icon: CheckCircle2 },
  { key: "earlyBird", label: "Early Bird", short: "Early Bird", color: T.info600, bg: T.info50, Icon: Sunrise },
  { key: "onLeave", label: "On Leave", short: "On Leave", color: T.purple, bg: T.purple50, Icon: Plane },
];

const STATUS_OPTS = [
  { value: "all", label: "All statuses" },
  { value: "notMarked", label: "Not Marked" },
  { value: "late", label: "Late" },
  { value: "onTime", label: "On Time" },
  { value: "earlyBird", label: "Early Bird" },
  { value: "onLeave", label: "On Leave" },
];

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/* 'HH:MM:SS' → '9:00 am'. Formatted from the string's own parts, never through
   Date parsing — a bare TIME has no date to anchor a timezone to. */
const fmtShiftTime = (t) => {
  const m = String(t || "").match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "";
  const h = Number(m[1]);
  const suffix = h < 12 ? "am" : "pm";
  return `${h % 12 || 12}:${m[2]} ${suffix}`;
};

/* The shift label shown in place of the old status tag. */
const shiftRange = (shift) => {
  if (!shift) return "";
  const from = fmtShiftTime(shift.start);
  const to = fmtShiftTime(shift.end);
  return from && to ? `${from} – ${to}` : from || to;
};

// scope 'company' — every employee (manage_attendance).
// scope 'team'    — only the viewer's team: direct reports + the people on the
//                   projects they manage (view_team_attendance). Same board.
const CompanyAttendance = ({ scope = "company" }) => {
  const isTeam = scope === "team";
  const token = localStorage.getItem("token");
  const [summary, setSummary] = useState({});
  const [groups, setGroups] = useState({});
  const [search, setSearch] = useState("");
  const [department, setDepartment] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [date, setDate] = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/attendance/${isTeam ? "team" : "company"}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date },
      });
      setSummary(res.data.summary || {});
      setGroups(res.data.groups || {});
    } catch (err) {
      console.error(`${isTeam ? "Team" : "Company"} attendance error`, err);
    } finally {
      setLoading(false);
    }
  }, [token, date, isTeam]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const matchesSearch = (emp) =>
    String(emp.username || emp.name || emp.email || emp.id).toLowerCase().includes(search.toLowerCase());
  const filterEmployees = (list = []) => (list || []).filter(matchesSearch);

  // Export currently-visible employees (respects search, department-N/A, status).
  const exportCSV = () => {
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = [];
    SECTIONS.forEach((sec) => {
      if (statusFilter !== "all" && statusFilter !== sec.key) return;
      filterEmployees(groups[sec.key]).forEach((e) => {
        // Shift is appended, not swapped in for Status: a CSV has no section
        // header to carry the status the way the on-screen list does.
        const shift = e.shift ? `${e.shift.name} (${shiftRange(e.shift)})` : "";
        rows.push([e.username || e.name || e.email, sec.short, e.check_in || "—", shift].map(escape).join(","));
      });
    });
    const csv = "Employee,Status,Check-In,Shift\n" + rows.join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
    a.download = `${isTeam ? "team" : "company"}-attendance-${date}.csv`;
    a.click();
  };

  const kpis = [
    { label: "Total", v: summary.total ?? 0, Icon: Users, c: T.fg1, bg: T.bgMuted },
    { label: "Not Marked", v: summary.notMarked ?? 0, Icon: Clock, c: T.fg5, bg: T.bgMuted },
    { label: "Late", v: summary.late ?? 0, Icon: AlarmClock, c: "#b45309", bg: "rgba(251,191,36,0.12)" },
    { label: "On Time", v: summary.onTime ?? 0, Icon: CheckCircle2, c: T.success600, bg: T.success50 },
    { label: "Early Bird", v: summary.earlyBird ?? 0, Icon: Sunrise, c: T.info600, bg: T.info50 },
  ];

  const card = { background: "var(--em-bg-card)", border: `1px solid ${T.border}`, borderRadius: 14, boxShadow: T.shadowSm };
  const dateInput = { width: 160, flex: "0 0 auto", padding: "9px 12px", border: `1px solid ${T.border}`, borderRadius: 8, font: `500 13px ${T.font}`, color: T.fg1, outline: "none", background: "var(--em-bg-card)", cursor: "pointer", boxSizing: "border-box" };
  const exportBtn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", borderRadius: 8, border: `1px solid ${T.border}`, background: "var(--em-bg-card)", cursor: "pointer", font: `500 12.5px ${T.font}`, color: T.fg1, whiteSpace: "nowrap" };

  const visibleSections = SECTIONS.filter((sec) => statusFilter === "all" || statusFilter === sec.key);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Filter toolbar */}
      <div style={{ ...card, padding: "16px 20px" }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={dateInput} />
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee…" style={{ flex: 1, minWidth: 180 }} />
          <Select value={department} onChange={(e) => setDepartment(e.target.value)} style={{ width: "auto", minWidth: 160 }}>
            <option value="all">All Departments</option>
            <option value="hr">HR</option>
            <option value="engineering">Engineering</option>
          </Select>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: "auto", minWidth: 150 }}>
            {STATUS_OPTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
          <button onClick={exportCSV} style={exportBtn}>
            <Download size={14} /> Export as CSV
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        {kpis.map((k) => (
          <div key={k.label} style={{ ...card, padding: "16px 18px" }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: k.bg, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
              <k.Icon size={15} color={k.c} />
            </div>
            <div style={{ font: `700 28px/1 ${T.font}`, color: T.fg1, letterSpacing: "-0.03em" }}>{k.v}</div>
            <div style={{ font: `500 11px ${T.font}`, color: T.fg5, marginTop: 4 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Sections */}
      {loading ? (
        <div style={{ ...card, padding: 40, textAlign: "center", color: T.fg5 }}>Loading…</div>
      ) : isTeam && !(summary.total > 0) ? (
        <div style={{ ...card, padding: "40px 24px", textAlign: "center" }}>
          <Users size={28} color={T.fg7} style={{ opacity: 0.6 }} />
          <div style={{ font: `600 15px ${T.font}`, color: T.fg1, marginTop: 10 }}>No team members yet</div>
          <div style={{ font: `400 13px/1.5 ${T.font}`, color: T.fg5, marginTop: 4 }}>
            Your team is the people who report to you and the people on the projects you manage.
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visibleSections.map((sec) => {
            const employees = filterEmployees(groups[sec.key]);
            const open = !collapsed[sec.key];
            return (
              <div key={sec.key} style={{ ...card, overflow: "hidden" }}>
                <button onClick={() => setCollapsed((s) => ({ ...s, [sec.key]: !s[sec.key] }))}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", background: "transparent", border: 0, cursor: "pointer", textAlign: "left" }}>
                  <div style={{ width: 32, height: 32, borderRadius: 9, background: sec.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <sec.Icon size={14} color={sec.color} />
                  </div>
                  <div style={{ flex: 1, font: `600 14px ${T.font}`, color: T.fg1 }}>{sec.label}</div>
                  <span style={{ padding: "3px 10px", borderRadius: 9999, background: sec.bg, color: sec.color, font: `700 12px ${T.font}` }}>{(groups[sec.key] || []).length}</span>
                  {open ? <ChevronUp size={16} color={T.fg7} /> : <ChevronDown size={16} color={T.fg7} />}
                </button>
                {open && (
                  <div style={{ borderTop: `1px solid ${T.divider}` }}>
                    {employees.length === 0 ? (
                      <div style={{ padding: "16px 20px", font: `400 13px ${T.font}`, color: T.fg7 }}>No employees in this category</div>
                    ) : (
                      employees.map((emp) => (
                        <div key={emp.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", borderBottom: `1px solid ${T.divider}` }}>
                          <Avatar name={emp.username || emp.name || ""} size={32} gradient />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ font: `600 13px ${T.font}`, color: T.fg1 }}>{emp.username || emp.name || emp.email}</div>
                            {emp.department && <div style={{ font: `400 11px ${T.font}`, color: T.fg5 }}>{emp.department}</div>}
                          </div>
                          {/* Check-in time only when there is one — no dash and
                              no reserved column, so unmarked rows stay quiet. */}
                          {emp.check_in && (
                            <div
                              style={{ font: `600 13px ${T.font}`, color: T.fg1, textAlign: "right" }}
                              title={
                                emp.scheduled_start
                                  ? `Scheduled ${fmtShiftTime(emp.scheduled_start)}`
                                  : "No shift assigned — judged against 9:30 am"
                              }
                            >
                              {/* The API sends a stable 'HH:MM'; the 12-hour
                                  rendering belongs here, like every other time
                                  on this page. */}
                              {fmtShiftTime(emp.check_in)}
                            </div>
                          )}
                          {/* The shift replaces the old status tag: the section
                              header already names the status, so the tag only
                              repeated it. Name only — the hours stay on hover
                              and in the CSV, where they don't crowd the row. */}
                          <span
                            title={emp.shift ? `${emp.shift.name} · ${shiftRange(emp.shift)}` : "No shift assigned"}
                            style={{ padding: "3px 10px", borderRadius: 9999, background: T.bgMuted, font: `600 11px ${T.font}`, color: emp.shift ? T.fg1 : T.fg7, whiteSpace: "nowrap", flexShrink: 0 }}
                          >
                            {emp.shift ? emp.shift.name : "No shift"}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CompanyAttendance;
